/**
 * Marquee Stack — 3 mega-type rows scrolling in opposite directions, middle row
 * gets a brand gradient. Optional overlay headline.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";

export const marqueeStack: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const items = (inputs.items?.length ? inputs.items : ["LOKAL · NÄHE · ECHT", "ALLYVER · ALLYVER", "DEINE STADT"]).slice(0, 3);
  const overlayHead = inputs.text ?? "";
  const overlaySub = inputs.subtext ?? "";

  const hero = typeSize("hero", ctx.format);
  const md = typeSize("md", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const rowIds = items.map((_, i) => `${clipPrefix}-row-${i}`);
  const tickIds = items.map((_, i) => `${clipPrefix}-tick-${i}`);
  const overlayId = `${clipPrefix}-overlay`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const rowsHtml = items.map((line, i) => {
    const isMid = i === 1;
    const text = line.repeat(4);
    const style = isMid
      ? `background:linear-gradient(90deg, ${accent} 0%, ${withAlpha(accent, 0.5)} 50%, ${accent} 100%);-webkit-background-clip:text;background-clip:text;color:transparent;`
      : `color:${withAlpha(themeColors.fg, i === 0 ? 0.92 : 0.55)};`;
    return `<div id="${rowIds[i]}" style="position:relative;overflow:hidden;height:${hero * 0.9}px;display:flex;align-items:center;width:100%;mask-image:linear-gradient(90deg, transparent, black 8%, black 92%, transparent);-webkit-mask-image:linear-gradient(90deg, transparent, black 8%, black 92%, transparent);">
      <div id="${tickIds[i]}" style="display:inline-flex;white-space:nowrap;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${hero * 0.8}px;letter-spacing:-3px;${style}">${escapeHtml(text)}</div>
    </div>`;
  }).join("");

  const overlayHtml = (overlayHead || overlaySub)
    ? `<div id="${overlayId}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;text-align:center;pointer-events:none;">
        ${overlayHead ? `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${hero * 0.45}px;letter-spacing:-2px;color:${themeColors.fg};background:${withAlpha(themeColors.bg, 0.6)};padding:18px 36px;border-radius:24px;backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border:1.5px solid ${withAlpha(themeColors.fg, 0.18)};text-wrap:balance;max-width:80%;">${escapeHtml(overlayHead)}</div>` : ""}
        ${overlaySub ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${md * 0.55}px;color:${withAlpha(themeColors.fg, 0.85)};max-width:780px;line-height:1.35;">${escapeHtml(overlaySub)}</div>` : ""}
      </div>`
    : "";

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;display:flex;flex-direction:column;justify-content:center;gap:8px;">
    ${rowsHtml}
    ${overlayHtml}
  </div>`;

  const init = [
    ...rowIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0 })),
    overlayHtml ? gsapSetLine(`#${overlayId}`, { opacity: 0, scale: 0.92 }) : "",
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  rowIds.forEach((id, i) => tweens.push(t.to(`#${id}`, { opacity: 1, duration: 0.5, ease: "raw:\"power1.out\"" }, i * 0.1)));
  // Scroll directions: 0 leftward, 1 rightward, 2 leftward
  tickIds.forEach((id, i) => {
    const dir = i % 2 === 0 ? -1 : 1;
    const distance = 800;
    tweens.push(`tl.fromTo(${JSON.stringify("#" + id)},{x:${-dir * distance / 2}},{x:${dir * distance / 2},duration:${clipDurationSec * 2.0},ease:"none",repeat:-1},${clipStartSec.toFixed(4)});`);
  });
  if (overlayHtml) tweens.push(t.to(`#${overlayId}`, { opacity: 1, scale: 1, duration: 0.7, ease: "raw:\"power2.out\"" }, 0.4));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
